import { NgClass, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { IconName } from '@sc/data-access-assets';
import { IconComponent } from '@sc/ui-components';
import {
  AlertDialogData,
  AlertDialogType,
  DialogButtonsChoice,
  DialogButtonsConfig,
} from '../../types';

@Component({
  selector: 'sc-dialog',
  styleUrls: ['./dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogModule,
    IconComponent,
    NgClass,
    NgIf,
    MatButtonModule,
    TranslateModule,
  ],
  template: `
    <div class="container">
      <div class="close-icon-container">
        <button mat-dialog-close="CLOSE">
          <sc-icon name="close"></sc-icon>
          <span class="sr_only">{{
            'GlobalApplicativeConstants.accessibility.closeDialog'
              | translate: { dialogTitle: title | translate }
          }}</span>
        </button>
      </div>
      <h1 mat-dialog-title id="custom-dialog-title-id">
        <sc-icon
          [ngClass]="type"
          [name]="getIcon().name"
          [specificName]="getIcon()?.specificName"
        ></sc-icon>
        <span>{{ title | translate }}</span>
      </h1>
      <div mat-dialog-content>
        <p>{{ description | translate }}</p>
      </div>
      <div mat-dialog-actions class="buttons">
        <button
          mat-stroked-button
          mat-dialog-close="CLOSE"
          [title]="
            'GlobalApplicativeConstants.accessibility.closeDialog'
              | translate: { dialogTitle: title | translate }
          "
          *ngIf="useButtons.length === 0 || buttonsInclude('CLOSE')"
        >
          {{ buttonsTranslate('CLOSE') | translate }}
        </button>
        <button
          mat-stroked-button
          mat-dialog-close="CANCEL"
          [title]="
            'GlobalApplicativeConstants.accessibility.closeDialog'
              | translate: { dialogTitle: title | translate }
          "
          *ngIf="buttonsInclude('CANCEL')"
        >
          {{ buttonsTranslate('CANCEL') | translate }}
        </button>
        <button
          mat-stroked-button
          mat-dialog-close="NO"
          *ngIf="buttonsInclude('NO')"
        >
          {{ buttonsTranslate('NO') | translate }}
        </button>
        <button
          mat-stroked-button
          mat-dialog-close="YES"
          *ngIf="buttonsInclude('YES')"
        >
          {{ buttonsTranslate('YES') | translate }}
        </button>
        <button
          mat-stroked-button
          mat-dialog-close="CONFIRM"
          *ngIf="buttonsInclude('CONFIRM')"
        >
          {{ buttonsTranslate('CONFIRM') | translate }}
        </button>
      </div>
    </div>
  `,
})
export class DialogComponent {
  type: AlertDialogType;
  title: string;
  description: string;

  useButtons: DialogButtonsConfig[] = [];

  constructor(@Inject(MAT_DIALOG_DATA) public data: AlertDialogData) {
    this.title = data.title;
    this.type = data.type;
    this.description = data.description;
    this.useButtons = data.useButtons || [];
  }

  buttonsInclude(buttonChoice: DialogButtonsChoice) {
    return this.useButtons
      .map((btn) => btn.buttonChoice)
      .includes(buttonChoice);
  }

  buttonsTranslate(buttonChoice: DialogButtonsChoice) {
    const foundBtn = this.useButtons.find(
      (btn) => btn.buttonChoice === buttonChoice
    );
    return foundBtn?.buttonText || `Dialog.Button${buttonChoice}`;
  }

  getIcon(): { name: IconName; specificName?: string } {
    switch (this.type) {
      case 'ERROR_ALERT':
        return {
          name: 'cancel_alt',
        };
      case 'WARN_ALERT':
        return {
          name: 'warning_circle',
          specificName: 'dialog_warning_circle',
        };
      case 'SUCCESS_ALERT':
        return {
          name: 'check',
        };
      case 'INFO_ALERT':
        return {
          name: 'info',
        };
      default:
        return {
          name: 'warning_circle',
        };
    }
  }
}
